// Reuse anywhere: attach drag + hover-to-end scroll to a container
import { useCallback, useEffect, useRef } from 'react';

export function useHorizontalDragHoverScroll(
  containerRef,
  {
    enableDragScroll = true,
    enableHoverScroll = true,
    dragSpeed = 2,
  } = {}
) {
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const setupDoneRef = useRef(false);
  const cleanupFnRef = useRef(null);
  const lastElementRef = useRef(null);

  const scrollToEnd = useCallback(() => {
    const el = containerRef.current;

    if (!el) {
      return;
    }

    const currentScroll = el.scrollLeft;
    const maxLeft = el.scrollWidth - el.clientWidth;

    // Calculate scroll amount for one "step" (approximately one prompt width)
    // Estimate prompt width + gap (around 150-200px per prompt)
    const scrollStep = 180; // Scroll by ~180px (one prompt + gap)

    // Calculate target scroll position
    const targetScroll = Math.min(currentScroll + scrollStep, maxLeft);
    const distance = targetScroll - currentScroll;

    // Only scroll if there's meaningful distance and we haven't reached the end
    if (Math.abs(distance) > 10 && currentScroll < maxLeft) {
      // Animate scroll manually for better control
      const duration = 800; // 800ms for slow, predictable scroll
      const startTime = performance.now();

      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Use easeOutCubic for smooth deceleration
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        const newScrollLeft = currentScroll + (distance * easeOutCubic);

        el.scrollLeft = newScrollLeft;

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };

      requestAnimationFrame(animateScroll);
    }
  }, [containerRef, enableHoverScroll]);

  useEffect(() => {
   // Clean up any existing setup
    if (cleanupFnRef.current) {
      cleanupFnRef.current();
    }
    setupDoneRef.current = false;
    cleanupFnRef.current = null;

    // Try multiple times to find the element
    let attempts = 0;
    const maxAttempts = 5;

    const trySetup = () => {
      const el = containerRef.current;
      attempts++;

      if (!el) {
        if (attempts < maxAttempts) {
          setTimeout(trySetup, 100);
        } 
        return;
      }

      if (!enableDragScroll) return;

      setupDoneRef.current = true;

      const onMouseDown = (e) => {
     
      isDraggingRef.current = true;
      startXRef.current = e.pageX - el.offsetLeft;
      scrollLeftRef.current = el.scrollLeft;
      e.preventDefault();
    };

    const onMouseMove = (e) => {
      if (!isDraggingRef.current) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      const walk = (x - startXRef.current) * dragSpeed;
      el.scrollLeft = scrollLeftRef.current - walk;
    
    };

    const endDrag = () => {
      isDraggingRef.current = false;
    };

    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('mousemove', onMouseMove);
    el.addEventListener('mouseleave', endDrag);
    el.addEventListener('mouseup', endDrag);

    // Touch support (optional)
    const onTouchStart = (e) => {
      isDraggingRef.current = true;
      startXRef.current = e.touches[0].pageX - el.offsetLeft;
      scrollLeftRef.current = el.scrollLeft;
    };
    const onTouchMove = (e) => {
      if (!isDraggingRef.current) return;
      const x = e.touches[0].pageX - el.offsetLeft;
      const walk = (x - startXRef.current) * dragSpeed;
      el.scrollLeft = scrollLeftRef.current - walk;
    };
    const onTouchEnd = () => {
      isDraggingRef.current = false;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);

      const cleanup = () => {
        el.removeEventListener('mousedown', onMouseDown);
        el.removeEventListener('mousemove', onMouseMove);
        el.removeEventListener('mouseleave', endDrag);
        el.removeEventListener('mouseup', endDrag);
        el.removeEventListener('touchstart', onTouchStart);
        el.removeEventListener('touchmove', onTouchMove);
        el.removeEventListener('touchend', onTouchEnd);
        setupDoneRef.current = false;
        cleanupFnRef.current = null;
      };

      cleanupFnRef.current = cleanup;
      return cleanup;
    };

    // Start the setup process
    trySetup();
  }); // Removed dependencies to force re-run on every render (debug mode)

  return { scrollToEnd };
}
